'use client';

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, User, Menu, X, Search, Heart, ChevronDown, Settings, LogOut, ArrowRight } from 'lucide-react';
import { useNotificationCenter } from '@/providers/NotificationCenterProvider';
import { useAuth } from '@/providers/AuthProvider';
import { useTheme } from '@/providers/ThemeProvider';
import { useWishlist } from '@/providers/WishlistProvider';
import { useStore } from '../context/StoreContext';
import { useBasePath } from '@/providers/BasePathProvider';
import { getInitials } from '@/lib/utils/format';
import { useRouter, usePathname } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { NotificationBell } from './NotificationBell';
import { CartDrawer } from './ui/CartDrawer';

export function Header() {
  const { config } = useTheme();
  const { user, isAuthenticated, logout } = useAuth();
  const { cart: cartItems, setCartOpen } = useStore();
  const { getWishlistCount } = useWishlist();
  const { closeDrawer } = useNotificationCenter();
  const wishlistCount = getWishlistCount?.() || 0;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { buildPath } = useBasePath();
  const router = useRouter();
  const pathname = usePathname();
  const searchRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close all dropdowns on navigation
  useEffect(() => {
    setIsSearchOpen(false);
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
    closeDrawer();
  }, [pathname, closeDrawer]);

  // Handle click outside search
  useEffect(() => {
    if (!isSearchOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSearchOpen]);

  // Handle click outside dropdown
  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      router.push(buildPath('/'));
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoggingOut(false);
      setIsDropdownOpen(false);
    }
  };

  const navLinks = [
    { href: buildPath('/shop'), label: 'Shop' },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-200 shadow-xs">
      <div className="container mx-auto px-4">
        <div className="relative flex items-center justify-between h-20">
          {/* Logo */}
          <Link href={buildPath('/')} className="flex items-center gap-3 z-10">
            <div className="relative w-12 h-12">
              <Image
                src={config.logo}
                alt={config.storeName}
                fill
                className="object-contain"
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8 z-10">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="text-gray-700 hover:text-hprimary-600 font-medium transition-colors relative group"
              >
                {link.label}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-hprimary-600 group-hover:w-full transition-all duration-300" />
              </Link>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-4 z-10">
            {/* Search */}
            <div className="relative" ref={searchRef}>
              <button
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Search"
              >
                <Search className="w-5 h-5 text-gray-700" />
              </button>

              <AnimatePresence>
                {isSearchOpen && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -10 }}
                    transition={{ duration: 0.15 }}
                    className="absolute sm:right-0 sm:top-full mt-2 bg-white rounded-lg shadow-lg overflow-hidden z-[60]"
                  >
                    <div className="flex items-center p-2 w-64">
                      <Search className="w-5 h-5 text-gray-400 mr-2 flex-shrink-0" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const sanitized = searchQuery.trim().replace(/\s+/g, ' ');
                            if (sanitized) {
                              router.push(buildPath(`/search?q=${encodeURIComponent(sanitized)}`));
                              setIsSearchOpen(false);
                              setSearchQuery('');
                            }
                          }
                        }}
                        placeholder="Search products..."
                        className="w-full outline-hidden text-gray-700 placeholder:text-gray-400"
                        autoFocus
                        autoComplete="off"
                      />
                      <AnimatePresence>
                        {searchQuery && (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => setSearchQuery('')}
                              className="text-gray-400 hover:text-gray-600 p-1"
                            >
                              <X className="w-4 h-4" />
                            </button>
                            <motion.button
                              initial={{ opacity: 0, scale: 0.8, x: -5 }}
                              animate={{ opacity: 1, scale: 1, x: 0 }}
                              exit={{ opacity: 0, scale: 0.8, x: -5 }}
                              onClick={() => {
                                const sanitized = searchQuery.trim().replace(/\s+/g, ' ');
                                if (sanitized) {
                                  router.push(buildPath(`/search?q=${encodeURIComponent(sanitized)}`));
                                  setIsSearchOpen(false);
                                  setSearchQuery('');
                                }
                              }}
                              className="p-1.5 bg-hprimary text-white rounded-md hover:bg-hprimary/90 transition-colors shadow-xs"
                            >
                              <ArrowRight className="w-3.5 h-3.5" />
                            </motion.button>
                          </div>
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Wishlist, Cart, and Notifications */}
            <div className="flex items-center gap-4">
              {/* Notifications - only show when authenticated */}
              {isAuthenticated && <NotificationBell />}

              <Link href={buildPath('/wishlist')} className="hidden lg:flex relative p-2 text-gray-700 hover:text-red-500 transition-colors">
                <Heart className="w-6 h-6" />
                {wishlistCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {wishlistCount}
                  </span>
                )}
              </Link>

              <button aria-label="Open cart" onClick={() => setCartOpen(true)} className="hidden lg:flex relative p-2 text-gray-700 hover:text-hprimary-600 transition-colors">
                <ShoppingCart className="w-6 h-6" />
                {cartItems.length > 0 ? (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {cartItems.length}
                  </span>
                ) : null}
              </button>

              {/* User Menu */}
              {isAuthenticated ? (
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-hprimary-100 text-xs font-semibold text-hprimary-700">
                      {getInitials(user?.firstname || '', user?.lastname || '')}
                    </div>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown Menu */}
                  <AnimatePresence>
                    {isDropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: -10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -10 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 top-full mt-2 w-56 rounded-lg border border-slate-200 bg-white shadow-lg z-[60] origin-top-right"
                      >
                        <div className="p-2">
                          <div className="px-3 py-2 border-b border-slate-200 mb-1">
                            <p className="text-sm font-medium text-slate-700 truncate">
                              {user?.firstname} {user?.lastname}
                            </p>
                            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                          </div>
                          <Link
                            href={buildPath('/account')}
                            onClick={() => setIsDropdownOpen(false)}
                            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 transition-colors"
                          >
                            <User className="h-4 w-4" />
                            My Account
                          </Link>
                          <div className="my-1 border-t border-slate-200" />
                          <button
                            onClick={handleLogout}
                            disabled={isLoggingOut}
                            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                          >
                            <LogOut className="h-4 w-4" />
                            {isLoggingOut ? 'Signing out...' : 'Sign Out'}
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  href={buildPath('/login')}
                  className="block px-4 py-2 bg-hprimary text-white rounded-lg hover:bg-hprimary-700 transition-colors font-medium"
                >
                  Sign In
                </Link>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu - Moved outside the flex container */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden overflow-hidden border-t border-gray-200 z-[50] top-16 mt-2"
            >
              <nav className="flex flex-col gap-1 py-2">
                {/* Wishlist and Cart - Mobile Only */}
                <Link
                  href={buildPath('/wishlist')}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg font-medium"
                >
                  <div className="flex items-center gap-3">
                    <Heart className="w-5 h-5" />
                    <span>Wishlist</span>
                  </div>
                  {wishlistCount > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                      {wishlistCount}
                    </span>
                  )}
                </Link>

                <button
                  onClick={() => {
                    setCartOpen(true);
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex w-full items-center justify-between px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg font-medium"
                >
                  <div className="flex items-center gap-3">
                    <ShoppingCart className="w-5 h-5" />
                    <span>Cart</span>
                  </div>
                  {cartItems.length > 0 ? (
                    <span className="bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                      {cartItems.length}
                    </span>
                  ) : null}
                </button>

                {/* Divider */}
                <div className="border-t border-gray-200 my-1" />

                {/* Navigation Links */}
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    className="px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg font-medium"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <CartDrawer />
    </header>
  );
}
